import { LinkCreationService, constructTableCells, getNameByStatus } from '@sportal365/smp-widget-utils';

// Types
import { IStanding, IStandingsConfig } from '../../../types/standings';

// Constants
import { assetsPlaceholder } from '../../../constants/assetsPlaceholder';
import { TARGET_BLANK, TARGET_SELF } from '../../../constants/windowTarget';
import { TEAM_ENTITY } from '../../../constants/entityTypes';

interface INormalizeStandingsResponseData {
  group: { type: string; name: string };
  standing: IStanding[];
}

export const normalizeStandingsResponseData = (
  sport: string,
  data?: INormalizeStandingsResponseData[],
  dataLabels?: IStandingsConfig['dataLabels'],
  dataEntityLinks?: IStandingsConfig['dataEntityLinks'],
  dataTeamNameType?: IStandingsConfig['dataTeamNameType'],
) => {
  if (!data || data.length === 0) return;

  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);
  const target = dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF;

  const getAllLabels = (standings: IStanding[]) => {
    return standings
      ?.flatMap(({ columns }) => columns)
      .filter((column, index, self) => index === self.findIndex((t) => t?.type.code === column?.type.code));
  };

  return data?.map(({ group, standing }) => ({
    group: { type: group?.type, name: group?.name },
    labels: constructTableCells(getAllLabels(standing), sport, dataLabels).labels,
    data: standing?.map(({ columns, team, form, rules }) => ({
      values: constructTableCells(columns, sport, dataLabels).values,
      team: {
        id: team?.id ?? '',
        name: getNameByStatus({
          name: team.name ?? '',
          short_name: team.short_name ?? '',
          three_letter_code: team.three_letter_code ?? '',
          status: dataTeamNameType,
        }),
        logo: team?.assets?.logo?.url ?? assetsPlaceholder.team,
        slug: team?.slug ?? '',
        withBadge: rules && rules.length > 0 ? true : false,
      },
      url: linkService?.createEntityUrl(team?.id.toString(), TEAM_ENTITY, '', team.slug) ?? '#',
      urlTarget: target,
      form: form.map(
        ({ id, outcome, start_time, slug, result, participants }) =>
          ({
            matchId: id,
            startTime: start_time,
            outcome: outcome,
            matchSummary:
              participants && participants.length > 0 && result && result.length > 0 && result[0].results.length > 0
                ? `${participants[0]?.name} ${result[0]?.results[0]?.value}:${result[0]?.results[1]?.value} ${participants[1]?.name}`
                : '',
            matchLink:
              participants && participants.length > 0 && id && linkService
                ? linkService?.createMatchUrl(String(id), participants[0], participants[1], slug) ?? '#'
                : '#',
            urlTarget: target,
          } ?? []),
      ),
    })),
    rules: standing
      ?.flatMap(({ rules }) => rules.map(({ id, name }) => ({ id: id, name })))
      .filter((rule, index, self) => index === self.findIndex((r) => r.name === rule.name)),
  }));
};
